import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { salesItemService, SalesItemDTO, UpdateSalesItemRequest } from '../../services/salesItemService';
import taxService, { Tax } from '../../services/taxService';
import { useCurrency } from '../../hooks/useCurrency';
import '../../styles/ImsTheme.css';
import '../Products/ProductCategoryEditPage.css';
import { parseApiError } from '../../utils/errorUtils';
import { ArrowLeft } from 'lucide-react';

interface FieldErrors {
  sellingPrice?: string;
  minimumSellingPrice?: string;
  returnPeriodDays?: string;
}

const SalesItemEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const defaultCurrency = useCurrency();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [item, setItem] = useState<SalesItemDTO | null>(null);
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [formData, setFormData] = useState<UpdateSalesItemRequest>({
    sellingPrice: 0,
    currency: 'USD',
    discountAllowed: true,
    returnAllowed: true,
    returnPeriodDays: 30,
    salesChannel: 'Store',
    isActive: true,
    taxId: undefined,
  });

  useEffect(() => {
    if (!id) {
      setError(t('common.noIdProvided'));
      setLoading(false);
      return;
    }
    loadData();
    taxService.getTaxes(1, 200).then(res => {
      const list: Tax[] = res?.data ?? [];
      setTaxes(list.filter(tx => tx.isActive && (tx.applicableFor === 'Sales' || tx.applicableFor === 'Both')));
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    // If item is loaded and currency is empty, use default
    if (item && !formData.currency) {
      setFormData(prev => ({ ...prev, currency: defaultCurrency }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCurrency, item]);

  const loadData = async () => {
    try {
      const response = await salesItemService.getSalesItemById(parseInt(id || '0'));
      if (response?.data?.data) {
        const data = response.data.data;
        setItem(data);
        setFormData({
          sellingPrice: data.sellingPrice,
          minimumSellingPrice: data.minimumSellingPrice,
          currency: data.currency,
          discountAllowed: data.discountAllowed,
          warrantyPeriod: data.warrantyPeriod,
          returnAllowed: data.returnAllowed,
          returnPeriodDays: data.returnPeriodDays,
          salesChannel: data.salesChannel,
          isActive: data.isActive,
          taxId: data.taxId ?? undefined,
        });
      }
      setLoading(false);
    } catch (err: unknown) {
      setError(parseApiError(err, t('salesItem.failedToLoadItem')).message);
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const isCheckbox = type === 'checkbox';
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: isCheckbox
        ? checked
        : name === 'taxId'
        ? (parseInt(value) || undefined)
        : name === 'returnPeriodDays'
        ? parseInt(value) || 0
        : ['sellingPrice', 'minimumSellingPrice'].includes(name)
        ? parseFloat(value) || 0
        : value,
    }));

    if (!isCheckbox) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    // Selling price >= 0
    if (formData.sellingPrice < 0) {
      errors.sellingPrice = t('salesItem.formValidation.sellingPriceNegative');
      isValid = false;
    }

    // Minimum selling price: >= 0 and must not exceed selling price
    if (formData.minimumSellingPrice !== undefined && formData.minimumSellingPrice !== null) {
      if (formData.minimumSellingPrice < 0) {
        errors.minimumSellingPrice = t('salesItem.formValidation.minimumSellingPriceNegative');
        isValid = false;
      } else if (formData.minimumSellingPrice > formData.sellingPrice) {
        errors.minimumSellingPrice = t('salesItem.formValidation.minPriceExceedsSelling');
        isValid = false;
      }
    }

    // Return period >= 0
    if (formData.returnAllowed && formData.returnPeriodDays !== undefined && formData.returnPeriodDays < 0) {
      errors.returnPeriodDays = t('salesItem.formValidation.returnPeriodNegative');
      isValid = false;
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    if (!validateForm()) return;

    setSaving(true);
    try {
      await salesItemService.updateSalesItem(parseInt(id || '0'), formData);
      navigate(`/sales-items/${id}`, { state: { message: t('salesItem.itemUpdatedSuccess') } });
    } catch (err: unknown) {
      setError(parseApiError(err, t('salesItem.failedToUpdateItem')).message);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="edit-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!item) {
    return (
      <Layout>
        <div className="edit-container">
          <div className="alert alert-danger">{error || t('salesItem.itemNotFound')}</div>
          <button className="btn btn-secondary" onClick={() => navigate('/sales-items')}>
            {t('salesItem.backToList')}
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="edit-container">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate(`/sales-items/${id}`)}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">
              {t('salesItem.editTitle')}
              <span className="ims-page-subtitle">{item.productName} — <code>{item.productSKU}</code></span>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate(`/sales-items/${id}`)} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={saving}>
              {saving ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : t('common.saveChanges')}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="edit-content">
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">{t('salesItem.itemDetails')}</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit} noValidate>

                  {/* Product (read-only) */}
                  <div className="form-group">
                    <label className="form-label">{t('salesItem.product')} (read-only)</label>
                    <input
                      type="text"
                      className="form-input"
                      value={`${item.productSKU} - ${item.productName}`}
                      disabled
                    />
                  </div>

                  {/* Selling Price */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('salesItem.sellingPrice')} <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      className={`form-input ${fieldErrors.sellingPrice ? 'is-invalid' : ''}`}
                      name="sellingPrice"
                      value={formData.sellingPrice}
                      onChange={handleInputChange}
                      step="0.01"
                    />
                    {fieldErrors.sellingPrice && <small className="form-error">{fieldErrors.sellingPrice}</small>}
                    {!fieldErrors.sellingPrice && <small className="form-helper">{t('salesItem.sellingPriceHint')}</small>}
                  </div>

                  {/* Minimum Selling Price */}
                  <div className="form-group">
                    <label className="form-label">{t('salesItem.minimumSellingPrice')}</label>
                    <input
                      type="number"
                      className={`form-input ${fieldErrors.minimumSellingPrice ? 'is-invalid' : ''}`}
                      name="minimumSellingPrice"
                      value={formData.minimumSellingPrice || ''}
                      onChange={handleInputChange}
                      step="0.01"
                    />
                    {fieldErrors.minimumSellingPrice && <small className="form-error">{fieldErrors.minimumSellingPrice}</small>}
                    {!fieldErrors.minimumSellingPrice && <small className="form-helper">{t('salesItem.minimumSellingPriceHint')}</small>}
                  </div>

                  {/* Currency */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('salesItem.currency')} <span className="required">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="JPY">JPY</option>
                      <option value="INR">INR</option>
                      <option value="LKR">LKR</option>
                    </select>
                  </div>

                  {/* Sales Channel */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('salesItem.salesChannel')} <span className="required">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="salesChannel"
                      value={formData.salesChannel}
                      onChange={handleInputChange}
                    >
                      <option value="Store">{t('salesItem.channelStore')}</option>
                      <option value="Online">{t('salesItem.channelOnline')}</option>
                      <option value="Wholesale">{t('salesItem.channelWholesale')}</option>
                      <option value="Distributor">{t('salesItem.channelDistributor')}</option>
                    </select>
                  </div>

                  {/* Tax */}
                  <div className="form-group">
                    <label className="form-label">{t('salesItem.tax')}</label>
                    <select
                      className="form-select"
                      name="taxId"
                      value={formData.taxId ?? ''}
                      onChange={handleInputChange}
                    >
                      <option value="">{t('salesItem.noTax')}</option>
                      {taxes.map((tax) => (
                        <option key={tax.taxId} value={tax.taxId}>
                          {tax.taxName} ({tax.taxPercentage}%)
                        </option>
                      ))}
                    </select>
                    {formData.taxId && (() => {
                      const selected = taxes.find(tx => tx.taxId === formData.taxId);
                      return selected ? (
                        <small className="form-helper">
                          {t('salesItem.taxHint')} &mdash; <strong>{selected.taxPercentage}%</strong>
                        </small>
                      ) : null;
                    })()}
                    {!formData.taxId && <small className="form-helper">{t('salesItem.taxHint')}</small>}
                  </div>

                  {/* Warranty Period */}
                  <div className="form-group">
                    <label className="form-label">{t('salesItem.warrantyPeriod')}</label>
                    <input
                      type="text"
                      className="form-input"
                      name="warrantyPeriod"
                      value={formData.warrantyPeriod || ''}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Discount Allowed */}
                  <div className="form-group">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="discountAllowed"
                        name="discountAllowed"
                        checked={formData.discountAllowed}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="discountAllowed">
                        {t('salesItem.discountAllowed')}
                      </label>
                    </div>
                  </div>

                  {/* Return Allowed */}
                  <div className="form-group">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="returnAllowed"
                        name="returnAllowed"
                        checked={formData.returnAllowed}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="returnAllowed">
                        {t('salesItem.returnAllowed')}
                      </label>
                    </div>
                  </div>

                  {/* Return Period Days */}
                  {formData.returnAllowed && (
                    <div className="form-group">
                      <label className="form-label">{t('salesItem.returnPeriodDays')}</label>
                      <input
                        type="number"
                        className={`form-input ${fieldErrors.returnPeriodDays ? 'is-invalid' : ''}`}
                        name="returnPeriodDays"
                        value={formData.returnPeriodDays || ''}
                        onChange={handleInputChange}
                      />
                      {fieldErrors.returnPeriodDays && <small className="form-error">{fieldErrors.returnPeriodDays}</small>}
                      {!fieldErrors.returnPeriodDays && <small className="form-helper">{t('salesItem.returnPeriodDaysHint')}</small>}
                    </div>
                  )}

                  {/* Active */}
                  <div className="form-group">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="isActive"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="isActive">
                        {t('salesItem.isActive')}
                      </label>
                    </div>
                  </div>


                </form>
              </div>
            </div>
          </div>

          <div className="edit-info-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('salesItem.productInformation')}</h3>
              </div>
              <div className="edit-card-body">
                <p><strong>{t('salesItem.productSKU')}:</strong> {item.productSKU}</p>
                <p><strong>{t('salesItem.productName')}:</strong> {item.productName}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SalesItemEditPage;
